import { useLogsByArea } from "@/hooks/useLogs";
import { IArea, ILevel } from "@/interfaces/level.interface";
import { IRequestLogs } from "@/interfaces/request.interface";
import { RootState } from "@/store/store";
import {
  convertDateString,
  convertDateWithoutTimeString,
} from "@/utils/common";
import { groupBy, GroupDescriptor } from "@progress/kendo-data-query";
import { Button } from "@progress/kendo-react-buttons";
import {
  setExpandedState,
  setGroupIds,
} from "@progress/kendo-react-data-tools";
import { Dialog } from "@progress/kendo-react-dialogs";
import {
  Grid,
  GridCellProps,
  GridColumn,
  GridPageChangeEvent
} from "@progress/kendo-react-grid";
import {
  Download,
  EyeIcon,
  FileSpreadsheet,
  FileText,
  PaperclipIcon
} from "lucide-react";
import React, { useEffect } from "react";
import { useSelector } from "react-redux";

const initialGroup: GroupDescriptor[] = [
  {
    field: "request.refNo",
  },
];

function ActivityLogs({
  handlePreview,
  area,
  level,
  imagePreviewWidth,
  imagePreviewHeight,
}: {
  handlePreview: any;
  area?: IArea;
  level: ILevel | null;
  imagePreviewWidth?: string;
  imagePreviewHeight?: string;
}) {
  const { user } = useSelector((state: RootState) => state.user);
  const [group, setGroup] = React.useState(initialGroup);

  // Add pagination state
  const [page, setPage] = React.useState({
    skip: 0,
    take: 10, // Number of items per page
  });

  const { data: response } = useLogsByArea(area?.id!, {
    enabled: !!area?.id,
  });

  const processWithGroups = (data: any, group: any) => {
    const newDataState = groupBy(data, group);
    setGroupIds({
      data: newDataState,
      group: group,
    });
    return newDataState;
  };

  const [resultState, setResultState] = React.useState([] as any);
  const [collapsedState, setCollapsedState] = React.useState([]);

  const onGroupChange = React.useCallback(
    (event: any) => {
      setGroup(event.group);
      setPage((prev) => ({ skip: 0, take: prev.take }));
    },
    []
  );

  const onExpandChange = React.useCallback(
    (event: any) => {
      const item = event.dataItem;
      if (item.groupId) {
        const newCollapsedIds = !event.value
          ? [...collapsedState, item.groupId]
          : collapsedState.filter((groupId) => groupId !== item.groupId);
        setCollapsedState(newCollapsedIds as any);
      }
    },
    [collapsedState]
  );

  // Handle page change
  const onPageChange = (event: GridPageChangeEvent) => {
    setPage({
      skip: event.page.skip,
      take: event.page.take,
    });
  };

  const newData = setExpandedState({
    data: resultState,
    collapsedIds: collapsedState,
  });

  useEffect(() => {
    if (response?.data) {
      const currentPageData = response.data.slice(page.skip, page.skip + page.take);
      const newDataState = processWithGroups(currentPageData, group);
      setResultState(newDataState);
    }
  }, [response?.data, group, page.skip, page.take]);

  const GroupCell = (props: any) => {
    return (
      props.tdProps && (
        <td
          {...props.tdProps}
          style={{
            backgroundColor: props.color,
          }}
        >
          {props.children && (
            <div className="flex items-center gap-2 font-semibold text-gray-700">
              {props.children.props.children[0]}
              {props.dataItem.items[0].request.refNo} -{" "}
              {convertDateWithoutTimeString(
                props.dataItem.items[0].request.from
              )}
              -
              {convertDateWithoutTimeString(props.dataItem.items[0].request.to)}
            </div>
          )}
        </td>
      )
    );
  };

  const GroupMyHeaderCustomCell = (props: GridCellProps) => (
    <GroupCell {...props} />
  );

  const Action = (props: GridCellProps) => {
    let textColor = "";

    switch (props.dataItem.action) {
      case "Area Accepted":
      case "Area Partially Accepted":
        textColor = "text-[#0095ff]";
        break;
      case "Area Assigned":
      case "Resubmitted":
      case "Reassigned":
      case "Overdue":
        textColor = "text-[#FF9800]";
        break;
      case "Area Released":
      case "Release Approved":
      case "Released":
        textColor = "text-[#219653]";
        break;
      case "Rejection Accepted":
      case "Rejected":
      case "Release Rejected":
        textColor = "text-[#EB5757]";
        break;
    }

    return (
      <td
        style={{ fontWeight: "700" }}
        className={`${textColor} uppercase font-black`}
      >
        {props.dataItem[props.field as string]}
      </td>
    );
  };

  const CreatedOn = (props: GridCellProps) => {
    return <td>{convertDateString(props.dataItem[props.field as string])}</td>;
  };

  const fileCell = (props: GridCellProps) => {
    const files = props.dataItem.requestTask.attachmentUrls;

    return (
      <td className="text-center">
        {files?.length > 0 && props.dataItem.action !== "Area Released" ? (
          <PaperclipIcon
            className="w-4 h-4 text-primary-500 cursor-pointer"
            onClick={() => handleFileClick(props.dataItem)}
          />
        ) : (
          <span className="text-gray-500">No files</span>
        )}
      </td>
    );
  };

  const [showFileDialog, setShowFileDialog] = React.useState(false);

  const [selectedItem, setSelectedItem] = React.useState<IRequestLogs | null>(
    null
  );
  const handleFileClick = (item: IRequestLogs) => {
    setSelectedItem(item);
    setShowFileDialog(true);
  };

  return (
    <>
      <div className="mt-4">
        <Grid
          style={{ width: "100%", height: "70vh" }}
          className="activity-logs"
          data={newData}
          groupable={true}
          onGroupChange={onGroupChange}
          group={group}
          onExpandChange={onExpandChange}
          expandField="expanded"
          cells={{
            group: {
              groupHeader: GroupMyHeaderCustomCell,
            },
          }}
          scrollable="scrollable"
          skip={page.skip}
          take={page.take}
          total={response?.data?.length || 0}
          pageable={{
            buttonCount: 5,
            info: true,
            type: "numeric",
            pageSizes: [5, 10, 15, 20],
            previousNext: true,
          }}
          onPageChange={onPageChange}
        >
          <GridColumn field="actionByName" title="Action By" width={150} />
          <GridColumn
            field="action"
            title="Action"
            cells={{
              data: Action,
            }}
            width={150}
          />
          <GridColumn
            field="actionOn"
            title="Action On"
            cells={{
              data: CreatedOn,
            }}
            width={165}
          />
          <GridColumn
            field="description"
            title="Comments"
            className="wrap-column"
          />
          <GridColumn
            field="files"
            title="Files"
            width={75}
            cells={{
              data: fileCell,
            }}
          />
        </Grid>
      </div>

      {showFileDialog && (
        <Dialog
          width={imagePreviewWidth}
          height={"80vh"}
          title={"Attachments"}
          onClose={() => setShowFileDialog(false)}
          className="image-dialog"
        >
          <div>
            <div className="grid grid-cols-4 gap-4">
              {selectedItem &&
                selectedItem.requestTask.attachmentUrls.length &&
                selectedItem.requestTask.attachmentUrls.map((file) => {
                  // Function to detect file type from URL
                  const getFileType = (url: any) => {
                    const extension = url.split(".").pop().toLowerCase();
                    if (extension === "pdf") {
                      return "pdf";
                    } else if (["xls", "xlsx", "csv"].includes(extension)) {
                      return "excel";
                    } else if (
                      ["jpg", "jpeg", "png", "gif", "webp"].includes(extension)
                    ) {
                      return "image";
                    } else {
                      return "unknown";
                    }
                  };

                  const fileType = getFileType(file);

                  if (fileType === "pdf" || fileType === "excel") {
                    return (
                      <div
                        key={file}
                        className="relative border rounded-md overflow-hidden group w-[100%] h-[150px] bg-gray-100 flex items-center justify-center"
                      >
                        {fileType === "pdf" ? (
                          <FileText size={48} className="text-primary-500" />
                        ) : (
                          <FileSpreadsheet
                            size={48}
                            className="text-primary-500"
                          />
                        )}

                        <div className="absolute bottom-2 left-0 right-0 text-center text-xs text-gray-600 truncate px-2">
                          {file.split("/").pop()}
                        </div>

                        <div className="absolute inset-0 flex items-center justify-center gap-2 bg-black/70 transition-all duration-500 opacity-0 invisible group-hover:opacity-100 group-hover:visible">
                          {/* <Button
                            className="rounded p-2 text-white border border-white"
                            fillMode={"outline"}
                            onClick={() => handlePreview(file)}
                          >
                            <EyeIcon size={16} className="w-4 h-4" />
                          </Button> */}
                          <Button
                            className="rounded p-2 text-white border border-white"
                            fillMode={"outline"}
                            onClick={() => {
                              // Handle file download here
                              const link = document.createElement("a");
                              link.href = file;
                              link.download = file.split("/").pop() || "file";
                              document.body.appendChild(link);
                              link.click();
                              document.body.removeChild(link);
                            }}
                          >
                            <Download size={16} className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    );
                  }

                  // Default image rendering (existing code)
                  return (
                    <div
                      key={file}
                      className="relative border rounded-md overflow-hidden group w-[100%] h-[150px]"
                    >
                      <img src={file} className="object-cover h-full w-full" />
                      <div className="absolute inset-0 flex items-center justify-center gap-2 bg-black/70 transition-all duration-500 opacity-0 invisible group-hover:opacity-100 group-hover:visible">
                        <Button
                          className="rounded p-2 text-white border border-white"
                          fillMode={"outline"}
                          onClick={() => handlePreview(file)}
                        >
                          <EyeIcon size={16} className="w-4 h-4" />
                        </Button>
                        <Button
                          className="rounded p-2 text-white border border-white"
                          fillMode={"outline"}
                          onClick={() => {
                            // Handle file download here
                            const link = document.createElement("a");
                            link.href = file;
                            link.download = file.split("/").pop() || "file";
                            document.body.appendChild(link);
                            link.click();
                            document.body.removeChild(link);
                          }}
                        >
                          <Download size={16} className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  );
                })}
            </div>
          </div>
        </Dialog>
      )}
    </>
  );
}

export default ActivityLogs;
